import React, { useState, useRef, useEffect } from 'react';
import { Button, Space, Typography } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined, SoundOutlined } from '@ant-design/icons';
import AudioWaveform from './AudioWaveform';
import { fetchSignedMediaUrl } from '../utils/media';

const { Text } = Typography;

interface AudioPlayerProps {
  audioId: string;
  deviceId: string;
  duration: number; // 秒単位
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ audioId, deviceId, duration: expectedDuration, onPlay, onPause, onEnded }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(expectedDuration);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  // 音声URLを取得
  useEffect(() => {
    const loadAudioUrl = async () => {
      if (audioUrl) return; // 既に取得済み
      
      setLoading(true);
      try {
        const { fetchAuthSession } = await import('../auth/gateway');
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();
        
        if (!token) return;
        
        const signedUrl = await fetchSignedMediaUrl(`/api/v1/devices/${deviceId}/audio/${audioId}/file`, token);
        setAudioUrl(signedUrl);
      } catch (error) {
        console.error('Failed to load audio:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadAudioUrl();
    
    // クリーンアップ不要（署名URLはブラウザ管理）
    return () => undefined;
  }, [audioId, deviceId, audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        setDuration(audio.duration);
      }
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      onEnded?.();
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [onEnded, audioUrl]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;

    try {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
        onPause?.();
      } else {
        await audio.play();
        setIsPlaying(true);
        onPlay?.();
      }
    } catch (error) {
      console.error('Audio playback error:', error);
    }
  };

  const handleSeek = (value: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = value;
      setCurrentTime(value);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{ width: '100%' }}>
      <audio
        ref={audioRef}
        src={audioUrl || undefined}
        preload="metadata"
      />
      
      <Space direction="vertical" style={{ width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button
            type="primary"
            shape="circle"
            icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={togglePlay}
            size="large"
            loading={loading}
            disabled={!audioUrl}
          />
          <SoundOutlined />
          <Text type="secondary">
            {formatTime(currentTime)} / {formatTime(duration)}
          </Text>
        </div>
        
        <AudioWaveform
          audioUrl={audioUrl || ''}
          currentTime={currentTime}
          duration={duration}
          onSeek={handleSeek}
        />
      </Space>
    </div>
  );
};

export default AudioPlayer;
